import { useMemo } from 'react';
import { Table } from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeHeader } from '@/hooks';
import { LoadingOverlay } from '@/components/feedback';
import { buildTableLabels } from '@/config/tableLabels';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetDeclarationAllocationsQuery } from '../api/declarationsApi';
import { buildUnitHeaderOptions } from '../constants/declarationUnits';
import { buildDeclarationAllocationColumns } from '../constants/declarationAllocationColumns';
import styles from './DeclarationAllocationsTabView.module.scss';

// TODO: add the info icons on the three headers that carry one once a Tooltip ships.

const getAllocationRowId = (row) => row.id;

/**
 * Props:
 *   declarationId  the declaration whose allocations the tab reads
 */
export const DeclarationAllocationsTabView = ({ declarationId }) => {
  const t = useTranslations();

  const { data: rows = EMPTY_LIST, isLoading } = useGetDeclarationAllocationsQuery({
    id: declarationId,
  });

  const { unitHeaderSelect, contextValue } = useUnitTypeHeader(rows, buildUnitHeaderOptions, t);

  const columns = useMemo(
    () => buildDeclarationAllocationColumns(t, { unitHeaderSelect }),
    [t, unitHeaderSelect],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  // Doc p.16: a draft, pending or processing declaration has allocated nothing yet.
  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('DECLARATIONS_ALLOCATIONS_EMPTY_TITLE'),
      description: t('DECLARATIONS_ALLOCATIONS_EMPTY_DESCRIPTION'),
    }),
    [t],
  );

  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  return (
    <div className={styles.tab}>
      <UnitTypeContext.Provider value={contextValue}>
        <Table
          columns={columns}
          data={rows}
          getRowId={getAllocationRowId}
          labels={tableLabels}
          emptyState={emptyState}
        />
      </UnitTypeContext.Provider>
    </div>
  );
};
